import { Spacing } from '@persona-id/design-tokens';
import { flatten } from 'lodash';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import styled from 'styled-components';

import {
  ButtonsContainer,
  LoadingShimmerTableRow,
} from 'dashboard/components/GraphExportModal/GraphExportModal';
import {
  ExportConfigStruct,
  GraphExportConfig,
} from 'dashboard/components/GraphExportModalV2/GraphExportConfig';
import { useGraphExplorer } from 'dashboard/components/GraphModule/hooks/useGraphExplorer';
import { useGraphQuerying } from 'dashboard/components/GraphModule/hooks/useGraphQuerying';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { AccountType } from 'dashboard/models/account-type';
import { NodeId } from 'dashboard/models/graph';
import { ServerNodeFunctionsConfig } from 'dashboard/models/graph-backend';
import {
  SelectedAccountsGraphExporter,
  SelectedAccountsGraphExporterPreview,
} from 'dashboard/models/graph-exporter';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Heading } from 'design-system/components/Text';
import EventsClient, { GraphEvent } from 'lib/clients/EventsClient';

const StyledModalBody = styled(ModalBody)`
  max-height: 600px;
  padding-bottom: ${Spacing[2]};
`;

enum ModalBodyContent {
  Preview = 'graph-export-preview',
  ColumnConfig = 'graph-export-column-config',
}

interface GraphExportModalV2Props {
  accountTypes: AccountType[];
  nodeFunctionsConfigs: ServerNodeFunctionsConfig[];
  exportNodes: NodeId[];
  // showGraphExportModal should never be true if the associated graph query
  // has been redacted or the results of the graph query is null
  showGraphExportModal: boolean;
  hideGraphExportModal: () => void;
}

const GraphExportModalV2 = ({
  exportNodes,
  hideGraphExportModal,
  showGraphExportModal,
  accountTypes,
  nodeFunctionsConfigs,
}: GraphExportModalV2Props) => {
  const graphExporter = useRef(new SelectedAccountsGraphExporter(true)).current;
  const csvRef = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null);

  const { graphExplorer } = useGraphExplorer();
  const { getHydratedNodes } = useGraphQuerying();

  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isLoadingCsv, setIsLoadingCsv] = useState(false);
  const [modalBodyContent, setModalBodyContent] = useState(ModalBodyContent.Preview);
  const [exportConfig, setExportConfig] = useState<ExportConfigStruct>({
    datumTypes: [],
    fieldsByAccountType: {},
  });
  const [result, setResult] = useState<Record<string, string | string[]>[]>([]);

  const closeGraphExportModal = useCallback(() => {
    setModalBodyContent(ModalBodyContent.Preview);
    setIsLoadingPreview(false);
    setIsLoadingCsv(false);
    setExportConfig({
      datumTypes: [],
      fieldsByAccountType: {},
    });
    hideGraphExportModal();
  }, [
    setModalBodyContent,
    setIsLoadingPreview,
    setIsLoadingCsv,
    setExportConfig,
    hideGraphExportModal,
  ]);

  useEffect(() => {
    setIsLoadingPreview(true);

    if (showGraphExportModal && getHydratedNodes) {
      if (!graphExporter) {
        setIsLoadingPreview(false);
        return;
      }

      graphExporter.generate(exportNodes, graphExplorer, getHydratedNodes, () => {
        setIsLoadingPreview(false);
      });
    }
  }, [
    showGraphExportModal,
    setIsLoadingPreview,
    exportNodes,
    graphExplorer,
    getHydratedNodes,
    graphExporter,
  ]);

  useEffect(() => {
    if (!isLoadingCsv) {
      return;
    }
    if (getHydratedNodes) {
      graphExporter.generateWithConfig(
        exportNodes,
        exportConfig,
        graphExplorer,
        accountTypes,
        getHydratedNodes,
        (result) => {
          setResult(result);
          setIsLoadingCsv(false);
        },
      );
    }
  }, [
    isLoadingCsv,
    accountTypes,
    getHydratedNodes,
    graphExporter,
    exportNodes,
    exportConfig,
    graphExplorer,
    setResult,
    setIsLoadingCsv,
  ]);

  useEffect(() => {
    if (result.length > 0) {
      // NOTE: Work around for CSVLink bug: https://github.com/react-csv/react-csv/issues/350
      csvRef.current?.link?.click();
      closeGraphExportModal();
      EventsClient.logAll(GraphEvent.GraphExportDownload);
    }
  }, [result, closeGraphExportModal]);

  return (
    <ModalWrapper
      isOpen={showGraphExportModal}
      handleCloseModal={closeGraphExportModal}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
    >
      <ModalContainer
        id='GraphExportModal__modal-container'
        size='xl'
        data-test='GraphExportModal__modal-container'
      >
        <ModalHeader data-test='GraphExportModal__modal-header' onClose={closeGraphExportModal}>
          Export Accounts
        </ModalHeader>
        <StyledModalBody>
          {modalBodyContent === ModalBodyContent.Preview && (
            <>
              <Heading size='sm'>Preview</Heading>
              <Spacer height={1} />
              {isLoadingPreview ? (
                [...Array(exportNodes.length)].map((_, idx) => {
                  return <LoadingShimmerTableRow key={idx} />;
                })
              ) : (
                <SelectedAccountsGraphExporterPreview exporter={graphExporter} />
              )}
            </>
          )}
          {modalBodyContent === ModalBodyContent.ColumnConfig && (
            <GraphExportConfig
              accountTypes={accountTypes}
              sources={flatten(nodeFunctionsConfigs.map((nfc) => nfc.sources))}
              onUpdateExportConfig={setExportConfig}
              exportConfig={exportConfig}
              datumTypes={nodeFunctionsConfigs.map((nfc) =>
                graphExplorer.getDatumTypeByToken(nfc['datum-type-token']),
              )}
            />
          )}
        </StyledModalBody>

        {modalBodyContent === ModalBodyContent.Preview && (
          <PreviewButtonActions
            handleOnClickCancel={closeGraphExportModal}
            handleOnClickNext={() => setModalBodyContent(ModalBodyContent.ColumnConfig)}
            isLoading={isLoadingPreview}
          />
        )}
        {modalBodyContent === ModalBodyContent.ColumnConfig && graphExporter && (
          <ColumnConfigButtonActions
            handleOnClickBack={() => setModalBodyContent(ModalBodyContent.Preview)}
            handleOnClickDownload={() => setIsLoadingCsv(true)}
            result={result}
            exporter={graphExporter}
            csvRef={csvRef}
            isLoading={isLoadingCsv}
          />
        )}
      </ModalContainer>
    </ModalWrapper>
  );
};

interface PreviewButtonActionsProps {
  handleOnClickCancel: () => void;
  handleOnClickNext: () => void;
  isLoading: boolean;
}

const PreviewButtonActions = ({
  handleOnClickCancel,
  handleOnClickNext,
  isLoading,
}: PreviewButtonActionsProps) => {
  return (
    <ButtonsContainer>
      <Button
        variant='basic'
        id='GraphExportModal__cancel-button'
        width='auto'
        onClick={handleOnClickCancel}
      >
        Cancel
      </Button>
      <Button
        variant='primary'
        id='GraphExportModal__next-button'
        width='auto'
        onClick={handleOnClickNext}
        isPending={isLoading}
        disabled={isLoading}
      >
        Next
      </Button>
    </ButtonsContainer>
  );
};

interface ColumnConfigButtonActionsProps {
  handleOnClickBack: () => void;
  handleOnClickDownload: () => void;
  exporter: SelectedAccountsGraphExporter;
  csvRef: RefObject<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>;
  result: Record<string, string | string[]>[];
  isLoading: boolean;
}

const ColumnConfigButtonActions = ({
  handleOnClickBack,
  handleOnClickDownload,
  result,
  csvRef,
  isLoading,
}: ColumnConfigButtonActionsProps) => {
  return (
    <ButtonsContainer>
      <Button
        width='auto'
        variant='basic'
        id='GraphExportModal__back-button'
        onClick={handleOnClickBack}
        disabled={isLoading}
      >
        Back
      </Button>
      <Button
        width='auto'
        variant='primary'
        id='GraphExportModal__back-button'
        onClick={handleOnClickDownload}
        isPending={isLoading}
        disabled={isLoading}
      >
        Download
      </Button>
      <CSVLink
        ref={csvRef}
        id='GraphExportModal__csv-download-button'
        data={result}
        target='_self'
        filename={`Graph Export (${new Date().toDateString()}).csv`}
      />
    </ButtonsContainer>
  );
};

export default GraphExportModalV2;
